/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {getEmissionTester} from '@ion/core/src/services/Connect';
import {EmissionTester} from '@ion/core/src/types/services';
import {QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseEmissionTesterResult = UseDefaultQueryResult & {
  emissionTester: EmissionTester;
  refresh: Function;
};

export interface UseEmissionTesterParams {
  id: number
};

/**
 * Retrieves the emission tester details for the given account id.
 *
 * @param id of the account to retrieve details for
 * @param enabled when true, the query will be executed immediately. Defaults to
 *          false in order to allow for updates to occur only at the user's discretion.
 * @param staleTime how long the query should hold onto details before re-fetching
 * @returns a query result
 */
export const useEmissionTester = (
  {id}: UseEmissionTesterParams,
  enabled = true,
  staleTime = getStaleTime(1, false, false)
): UseEmissionTesterResult => {
  const queryClient = useQueryClient();
  const query = useQuery([QueryKey.EMISSION_TESTER_BY_ID, id], async() => {
    try {
      const results = await getEmissionTester(id);
      return results;
    } catch (err) {
      error(err);
      return {};
    }
  }, {
    retry     : false,
    staleTime : staleTime as number,
    enabled
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries([QueryKey.EMISSION_TESTER_BY_ID, id]);
  }, [id]);

  return createQueryResult(query, {
    emissionTester : query.data,
    refresh
  }) as UseEmissionTesterResult;
};

export default useEmissionTester;
